import moment from "moment-timezone";
import { Flex, List, Space, Table, Tag, Typography } from "antd";
import { DATE_FORMAT } from "common/constants";
import currency from "currency.js";
import { useFetchPaymentIntentsQuery } from "features/consumerPortalUpcomingPayments/paymentIntentsAPI";
import styled from "styled-components";
import { camelToWords, snakeToCamelCase } from "common/utils";
import useIsMobile from "hooks/useIsMobile";

const StyledList = styled(List)`
  margin-top: 12px;
  border-radius: 8px;
  overflow: hidden;
  .ant-list-item {
    background: white;
    padding: 12px;
  }
  .ant-list-item:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const MethodSpace = styled(Space)`
  text-align: right;
`;

const FullWidthFlex = styled(Flex)`
  width: 100%;
`;

function PaymentHistory() {
  const { data: accounts = [], isLoading: isAccountsLoading } = useFetchPaymentIntentsQuery({});
  const isMobile = useIsMobile();

  const columns = [
    {
      title: "Scheduled Date",
      dataIndex: "scheduledDate",
      key: "scheduledDate",
      render: (_, record) =>
        record.scheduledDate && moment(record.scheduledDate).format(DATE_FORMAT),
    },
    {
      title: "Account ID(s)",
      dataIndex: "accountExternalIds",
      key: "accountExternalIds",
      render: (_, record) => (record.accountExternalIds ?? []).join(", "),
    },
    {
      title: "Payment Method",
      dataIndex: "method",
      key: "method",
      render: (_, record) => {
        if (!record?.paymentMethod) {
          // NOTE: If the payment method is None, then we assume it was paid by a codebtor.
          return "Payment from co-debtor";
        }
        if (record?.paymentMethod.type === "process_card" || record?.paymentMethod.type === "ach") {
          return (
            <Space>
              {record?.paymentMethod?.type === "process_card" && (
                <span>{`Credit Card ****${record?.paymentMethod.cardLast4Digits}`}</span>
              )}
              {record?.paymentMethod?.type === "process_ach" && (
                <span>{`Bank Account ****${record?.paymentMethod.bankAccountLast4Digits}`}</span>
              )}
              {record?.refundedAt && <Tag color="blue">REFUNDED</Tag>}
              {record?.voidedAt && <Tag color="yellow">VOIDED</Tag>}
            </Space>
          );
        }

        return `Paid by ${camelToWords(snakeToCamelCase(record?.paymentMethod?.type))}`;
      },
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (_, record) => currency(record.totalAmount, { precision: 2 }).format(),
    },
    // {
    //   title: "",
    //   dataIndex: "action",
    //   key: "action",
    //   render: () => (
    //     <Space size="middle">
    //       <Button type="link">Cancel</Button>
    //     </Space>
    //   ),
    // },
  ];

  const executedAccounts = accounts.filter((account) => account.executedAt);

  return (
    <>
      <h3>Payment History</h3>
      {isMobile ? (
        <MobilePaymentHistoryView accounts={executedAccounts} isLoading={isAccountsLoading} />
      ) : (
        <Table
          scroll={{ x: "max-content" }}
          loading={isAccountsLoading}
          dataSource={executedAccounts}
          // @ts-ignore
          columns={columns}
          pagination={{
            hideOnSinglePage: true,
            pageSize: 5,
          }}
        />
      )}
    </>
  );
}

function MobilePaymentHistoryView({ accounts, isLoading }) {
  return (
    <StyledList
      loading={isLoading}
      dataSource={accounts}
      pagination={{
        pageSize: 5,
        hideOnSinglePage: true,
      }}
      renderItem={(record) => {
        /** @type {any} */
        const item = record;
        const methodText = (() => {
          if (!item?.paymentMethod) {
            return "Payment from co-debtor";
          }
          if (item?.paymentMethod.type === "process_card" || item?.paymentMethod.type === "ach") {
            if (item?.paymentMethod?.type === "process_card") {
              return `Credit Card ****${item?.paymentMethod.cardLast4Digits}`;
            }
            if (item?.paymentMethod?.type === "process_ach") {
              return `Bank Account ****${item?.paymentMethod.bankAccountLast4Digits}`;
            }
          }
          return `Paid by ${camelToWords(snakeToCamelCase(item?.paymentMethod?.type))}`;
        })();
        return (
          <List.Item key={item.uuid}>
            <FullWidthFlex justify="space-between" align="flex-start" gap={16}>
              <Flex vertical>
                <Typography.Text strong>
                  {item.scheduledDate && moment(item.scheduledDate).format(DATE_FORMAT)}
                </Typography.Text>
                <Typography.Text type="secondary">
                  {(item.accountExternalIds ?? []).join(", ") || "No Account IDs"}
                </Typography.Text>
              </Flex>
              <Flex vertical align="flex-end">
                <MethodSpace size={4}>
                  <Typography.Text>{methodText}</Typography.Text>
                  {item?.refundedAt && <Tag color="blue">REFUNDED</Tag>}
                  {item?.voidedAt && <Tag color="yellow">VOIDED</Tag>}
                </MethodSpace>
                <Typography.Text strong>
                  {currency(item.totalAmount, { precision: 2 }).format()}
                </Typography.Text>
              </Flex>
            </FullWidthFlex>
          </List.Item>
        );
      }}
    />
  );
}

export default PaymentHistory;
function snakeToCamel(type) {
  throw new Error("Function not implemented.");
}
